import { cx } from 'cvb';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import constants from '../../../../config/constants';
import _ from '../../../../utils/LodashImports';
import { noop } from '../../../../utils/noop';
import { mapATGPricesToCatalystPrices } from '../../../../utils/PriceHelpers';
import { withUser } from '../../../../utils/UserContext';
import Prices from '../../../Prices/Prices';
import BodyCopy from '../../../ui/BodyCopy/BodyCopy';
import { ProductCounter } from '../../../ui/ProductCounter/ProductCounter';
import { CartItemStyles } from '../CartItem.style';
import CartItemActionLabel from './CartItemActionLabel';

const WholesaleBanner = dynamic(() => import('../../../WholesaleBanner/WholesaleBanner'), {
  ssr: false,
});

const CartItemAdditionalInformation = ({
  viewConfig,
  brandName,
  name,
  variantDetails,
  isMobile,
  productId,
  currentVariant,
  isAtgPrice,
  price,
  disableQuantitySelection,
  stepperStyle,
  totalQuantity,
  handleRemoveItem,
  handleAddItem,
  getMaxForStepper,
  inputVal,
  handleQuantityInput,
  handleInputChange,
  getMaxForProductCounter,
  showButton,
  labels,
  showMaxQuantityAlert,
  maxOnCartAmount,
  errorAddingToCart,
  addedQuantity,
  appCtx,
  item,
  userSegments,
  isUserLoggedIn,
}) => {
  const { regionCode } = appCtx;

  const toggles = _.get(appCtx, 'siteConfig.toggles', {});
  const isNewPriceApplicable = _.get(toggles, 'isNewPriceApplicable', false);
  const isQuantityEditableEnabled = _.get(toggles, 'isQuantityEditableEnabled', false);
  const isWholesaleEnabled = _.get(toggles, 'isWholesaleEnabled', false);

  const itemIsPdpProduct = item.skuId === currentVariant.id;

  const cesSegment = _.get(appCtx, 'siteConfig.configurations.CES_SEGMENT', {});
  const userCESSegment = cesSegment[userSegments?.find((segment) => cesSegment[segment.name])?.name];
  const cesPrice = isUserLoggedIn
    ? currentVariant?.promotions?.find((promotion) => promotion.campaignName === userCESSegment) || null
    : null;

  const proPrice = itemIsPdpProduct
    ? currentVariant?.promotions?.find(
        (promotion) =>
          promotion.campaignName === constants.PRICE_TYPE.pro ||
          promotion.campaignName === constants.PRICE_TYPE.bulkPrice
      ) || null
    : null;
  const proPriceOperatorClass = {
    '=': 'equal',
    '>=': 'greaterOrEqual',
  }[proPrice?.metadata?.quantityToBuyOperator];

  const filteredPrices = currentVariant?.prices?.filter((_price) => _price.type !== constants.PRICE_TYPE.CMR) || [];
  const lowestPrice = filteredPrices?.length
    ? filteredPrices.reduce((min, _price) => (parseFloat(_price.price[0]) > parseFloat(min.price[0]) ? min : _price))
    : null;
  const proPriceIsLower =
    parseFloat(proPrice?.prices[0]?.price[0]) < parseFloat(lowestPrice?.price[0]) &&
    (cesPrice ? parseFloat(proPrice?.prices[0]?.price[0]) < parseFloat(cesPrice?.prices[0]?.price[0]) : true);

  return (
    <>
      <div className="additional-information">
        <div className="item-info">
          <BodyCopy type={viewConfig.brandName.type} size={viewConfig.brandName.size}>
            {brandName && brandName.toUpperCase()}
          </BodyCopy>
          <BodyCopy type={viewConfig.description.type} size={viewConfig.description.size} ellipsis maxlines={2}>
            {name}
          </BodyCopy>

          {variantDetails && (
            <BodyCopy type={viewConfig.description.type} size={viewConfig.description.size} ellipsis maxlines={2}>
              {variantDetails}
            </BodyCopy>
          )}
        </div>
        <div className={cx('price', isNewPriceApplicable && isMobile && 'mt-8px')}>
          <Prices
            key={productId}
            productId={item.productId}
            variantId={item.skuId}
            priceList={isAtgPrice ? mapATGPricesToCatalystPrices(price, regionCode, toggles) : price}
            variant={viewConfig.price}
            page="CART"
          />
        </div>
        {!isMobile && (
          <div>
            {isWholesaleEnabled && proPrice && proPriceIsLower && !disableQuantitySelection && (
              <div className="wholesale-banner-container">
                <WholesaleBanner
                  inputVal={inputVal}
                  onClick={({ quantity }) => handleAddItem(quantity)}
                  quantityToBuyValue={Number(proPrice?.metadata?.quantityToBuyValue)}
                  quantityToBuyOperator={proPrice.campaignName === constants.PRICE_TYPE.pro ? '>=' : '='}
                  staticHeight
                />
              </div>
            )}
            <div className="stepper-and-label-container-desktop">
              {!disableQuantitySelection && (
                <div className="stepper" style={stepperStyle}>
                  {totalQuantity >= 0 && (
                    <ProductCounter
                      count={isQuantityEditableEnabled ? inputVal : totalQuantity}
                      className="product-count-desktop"
                    >
                      <ProductCounter.Decrement
                        className="decrement-desktop"
                        minimum={0}
                        onClick={() => handleRemoveItem(undefined, undefined, undefined, true)}
                        type="primary"
                      />
                      {isQuantityEditableEnabled ? (
                        <ProductCounter.CountInput
                          className="product-count-value-desktop'"
                          handleInputBlurKey={handleQuantityInput}
                          handleOnChange={handleInputChange}
                          maximum={getMaxForProductCounter()}
                          minimum={0}
                        />
                      ) : (
                        <ProductCounter.CountText type="primary" />
                      )}
                      <ProductCounter.Increment
                        className="increment-desktop"
                        maximum={getMaxForStepper()}
                        onClick={() => handleAddItem(undefined, true)}
                        type="primary"
                      />
                    </ProductCounter>
                  )}
                </div>
              )}
              <CartItemActionLabel
                showButton={showButton}
                totalQuantity={totalQuantity}
                handleAddItem={handleAddItem}
                labels={labels}
                showMaxQuantityAlert={showMaxQuantityAlert}
                maxOnCartAmount={maxOnCartAmount}
                errorAddingToCart={errorAddingToCart}
                addedQuantity={addedQuantity}
                appCtx={appCtx}
              />
            </div>
          </div>
        )}
        {isMobile && (
          <div className="cart-item cart-item-mobile">
            {!disableQuantitySelection && (
              <div>
                <div
                  className={cx(
                    'stepper-mobile',
                    isWholesaleEnabled && proPriceIsLower && 'proPrice',
                    proPriceOperatorClass
                  )}
                  style={stepperStyle}
                >
                  {totalQuantity >= 0 && (
                    <>
                      {isWholesaleEnabled && proPrice && proPriceIsLower && (
                        <div className="wholesale-banner-container">
                          <WholesaleBanner
                            inputVal={inputVal}
                            onClick={({ quantity }) => handleAddItem(quantity)}
                            quantityToBuyValue={Number(proPrice?.metadata?.quantityToBuyValue)}
                            quantityToBuyOperator={proPrice.campaignName === constants.PRICE_TYPE.pro ? '>=' : '='}
                            staticHeight
                          />
                        </div>
                      )}
                      <ProductCounter
                        count={isQuantityEditableEnabled ? inputVal : totalQuantity}
                        className="product-count-mobile"
                      >
                        <ProductCounter.Decrement
                          className="decrement-mobile"
                          minimum={0}
                          onClick={handleRemoveItem}
                          type="primary"
                        />
                        {isQuantityEditableEnabled ? (
                          <ProductCounter.CountInput
                            className="product-count-value-mobile"
                            handleInputBlurKey={handleQuantityInput}
                            handleOnChange={handleInputChange}
                            maximum={getMaxForProductCounter()}
                            minimum={0}
                          />
                        ) : (
                          <ProductCounter.CountText type="primary" />
                        )}
                        <ProductCounter.Increment
                          className="increment-mobile"
                          maximum={getMaxForStepper()}
                          onClick={handleAddItem}
                          type="primary"
                        />
                      </ProductCounter>
                    </>
                  )}
                </div>
                <CartItemActionLabel
                  showButton={showButton}
                  totalQuantity={totalQuantity}
                  handleAddItem={handleAddItem}
                  labels={labels}
                  showMaxQuantityAlert={showMaxQuantityAlert}
                  maxOnCartAmount={maxOnCartAmount}
                  errorAddingToCart={errorAddingToCart}
                  addedQuantity={addedQuantity}
                  appCtx={appCtx}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <style jsx>{CartItemStyles}</style>
    </>
  );
};

CartItemAdditionalInformation.defaultProps = {
  brandName: '',
  name: '',
  variantDetails: '',
  isMobile: false,
  productId: '',
  currentVariant: {},
  isAtgPrice: true,
  disableQuantitySelection: false,
  stepperStyle: {},
  totalQuantity: 0,
  handleRemoveItem: noop,
  handleAddItem: noop,
  getMaxForStepper: noop,
  inputVal: 0,
  handleQuantityInput: noop,
  handleInputChange: noop,
  getMaxForProductCounter: noop,
  showButton: false,
  labels: {},
  showMaxQuantityAlert: false,
  maxOnCartAmount: 0,
  errorAddingToCart: '',
  addedQuantity: 0,
  userSegments: [],
  isUserLoggedIn: false,
};

CartItemAdditionalInformation.propTypes = {
  viewConfig: PropTypes.object.isRequired,
  brandName: PropTypes.string,
  name: PropTypes.string,
  variantDetails: PropTypes.string,
  isMobile: PropTypes.bool,
  productId: PropTypes.string,
  currentVariant: PropTypes.object,
  isAtgPrice: PropTypes.bool,
  price: PropTypes.array.isRequired,
  disableQuantitySelection: PropTypes.bool,
  stepperStyle: PropTypes.object,
  totalQuantity: PropTypes.number,
  handleRemoveItem: PropTypes.func,
  handleAddItem: PropTypes.func,
  getMaxForStepper: PropTypes.func,
  inputVal: PropTypes.number,
  handleQuantityInput: PropTypes.func,
  handleInputChange: PropTypes.func,
  getMaxForProductCounter: PropTypes.func,
  showButton: PropTypes.bool,
  labels: PropTypes.object,
  showMaxQuantityAlert: PropTypes.bool,
  maxOnCartAmount: PropTypes.number,
  errorAddingToCart: PropTypes.string,
  addedQuantity: PropTypes.number,
  appCtx: PropTypes.object.isRequired,
  item: PropTypes.object.isRequired,
  userSegments: PropTypes.array,
  isUserLoggedIn: PropTypes.bool,
};

export default withUser(CartItemAdditionalInformation);
